import moment, { Moment, MomentInput } from 'moment'
import React, { startTransition, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import SetupExpenseReportCycleModal from 'src/component-modal/SetupExpenseReportCycleModal/SetupExpenseReportCycleModal'
import { UtilityToolbarDropdownChildProps } from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import {
  DateRangeType,
  resolveDateRangeTypeWindow,
} from 'src/components/DateFilterDropdown/dateRangeType'
import DateRangePresetMenu, {
  DateRangePresetOption,
} from 'src/components/DateRangePresetMenu/DateRangePresetMenu'
import { useTranslate } from 'src/localization/Trns'
import { OrgExpenseReport } from 'src/models/orgExpenseReports.models'
import { formatMonthDayRange } from 'src/utils/date.utils'

import style from './DateFilterDropdown.style'

interface DateFilterDropdownProps extends UtilityToolbarDropdownChildProps {
  dateRangeType?: DateRangeType
  handleSetType: (type: DateRangeType | undefined) => void
  handleSetRange: (since: Moment, until: Moment) => void
  handleSetSince: (val: Moment | undefined) => void
  handleSetUntil: (val: Moment | undefined) => void
  customSince: Moment | undefined
  customUntil: Moment | undefined
  showStatementOptions: boolean | undefined
  cycleId?: string
  currentStatement?: OrgExpenseReport
  previousStatement?: OrgExpenseReport
}

/**
 * Transactions/accounting date filter. Owns the statement-cycle presets and the
 * date-window resolution; renders the shared {@link DateRangePresetMenu} for the
 * actual menu chrome (preset rows, custom calendar, reset footer) so it stays
 * visually identical to the reporting dashboard's date picker.
 */
const DateFilterDropdown: React.FC<DateFilterDropdownProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const [showSetupCyclePopup, setShowSetupCyclePopup] = useState(false)

  const needsCycleSetup =
    props.showStatementOptions && props.currentStatement === undefined

  const formatRange = (
    startInput?: MomentInput,
    endInput?: MomentInput,
    fallback = '',
  ): string => {
    if (!startInput || !endInput) return fallback
    const start = moment(startInput)
    const end = moment(endInput)
    if (!start.isValid() || !end.isValid()) return fallback

    return formatMonthDayRange(start, end)
  }

  const monthRangeText = (m: moment.Moment) =>
    formatRange(m.clone().startOf('month'), m.clone().endOf('month'))

  useEffect(() => {
    if (
      !props.showStatementOptions &&
      (props.dateRangeType === DateRangeType.CurrentStatement ||
        props.dateRangeType === DateRangeType.PreviousStatement)
    ) {
      // switch to custom if user was previously on statement option
      props.handleSetType(DateRangeType.Custom)
    }
  }, [props.showStatementOptions])

  const handleSelectOption = (key: string): void => {
    const type = key as DateRangeType

    flushSync(() => {
      props.close?.()
    })

    startTransition(() => {
      props.handleSetType(type)

      switch (type) {
        case DateRangeType.Last30Days:
        case DateRangeType.Today:
        case DateRangeType.ThisMonth:
        case DateRangeType.LastMonth:
        case DateRangeType.Last90Days: {
          const rangeWindow = resolveDateRangeTypeWindow({
            dateRangeType: type,
          })
          if (rangeWindow) {
            props.handleSetRange(rangeWindow.since, rangeWindow.until)
          }
          break
        }
        case DateRangeType.CurrentStatement: {
          if (needsCycleSetup) {
            setShowSetupCyclePopup(true)
          } else {
            props.handleSetType(DateRangeType.CurrentStatement)
            const rangeWindow = resolveDateRangeTypeWindow({
              dateRangeType: DateRangeType.CurrentStatement,
              currentStatement: props.currentStatement,
            })
            if (rangeWindow) {
              props.handleSetRange(rangeWindow.since, rangeWindow.until)
            }
          }
          break
        }
        case DateRangeType.PreviousStatement: {
          if (needsCycleSetup) {
            setShowSetupCyclePopup(true)
          } else {
            props.handleSetType(DateRangeType.PreviousStatement)
            const rangeWindow = resolveDateRangeTypeWindow({
              dateRangeType: DateRangeType.PreviousStatement,
              previousStatement: props.previousStatement,
            })
            if (rangeWindow) {
              props.handleSetRange(rangeWindow.since, rangeWindow.until)
            }
          }
          break
        }
        case DateRangeType.Custom:
          break
      }
    })
  }

  const handleReset = (): void => {
    flushSync(() => {
      props.close?.()
    })

    startTransition(() => {
      props.handleSetType(DateRangeType.Last30Days)
      const rangeWindow = resolveDateRangeTypeWindow({
        dateRangeType: DateRangeType.Last30Days,
      })
      if (rangeWindow) {
        props.handleSetRange(rangeWindow.since, rangeWindow.until)
      }
    })
  }

  const handleSetSince = (val: Moment | undefined): void => {
    props.handleSetType(DateRangeType.Custom)
    props.handleSetSince(val)
  }

  const handleSetUntil = (val: Moment | undefined): void => {
    props.handleSetType(DateRangeType.Custom)
    props.handleSetUntil(val)
    props.close?.()
  }

  const now = moment()
  const today = now.format('MMM D, YYYY')
  const thisMonthText = monthRangeText(now)
  const lastMonthText = monthRangeText(now.clone().subtract(1, 'month'))

  const setupFallback = translate('Set up your card account statement cycle')

  const currentStatementText = props.currentStatement
    ? formatRange(
        props.currentStatement.since,
        props.currentStatement.until,
        setupFallback,
      )
    : setupFallback

  const previousStatementText = props.previousStatement
    ? formatRange(
        props.previousStatement.since,
        props.previousStatement.until,
        setupFallback,
      )
    : setupFallback

  const customRangeText = formatRange(props.customSince, props.customUntil)

  const options: DateRangePresetOption[] = [
    {
      key: DateRangeType.Today,
      label: DateRangeType.Today,
      subLabel: today,
      isActive: props.dateRangeType === DateRangeType.Today,
    },
    {
      key: DateRangeType.ThisMonth,
      label: DateRangeType.ThisMonth,
      subLabel: thisMonthText,
      isActive: props.dateRangeType === DateRangeType.ThisMonth,
    },
    {
      key: DateRangeType.LastMonth,
      label: DateRangeType.LastMonth,
      subLabel: lastMonthText,
      isActive: props.dateRangeType === DateRangeType.LastMonth,
    },
    ...(props.showStatementOptions
      ? [
          {
            key: DateRangeType.CurrentStatement,
            label: DateRangeType.CurrentStatement,
            subLabel: currentStatementText,
            isActive: props.dateRangeType === DateRangeType.CurrentStatement,
          },
        ]
      : []),
    // we want to hide this if there is no previous report but the cycle has been setup
    ...((props.showStatementOptions && props.previousStatement !== undefined) ||
    needsCycleSetup
      ? [
          {
            key: DateRangeType.PreviousStatement,
            label: DateRangeType.PreviousStatement,
            subLabel: previousStatementText,
            isActive: props.dateRangeType === DateRangeType.PreviousStatement,
          },
        ]
      : []),
  ]

  return (
    <div className="DateFilterDropdown" css={style}>
      <DateRangePresetMenu
        options={options}
        onSelectOption={handleSelectOption}
        custom={{
          label: translate('Custom Date Range'),
          isActive: props.dateRangeType === DateRangeType.Custom,
          activeSubLabel: customRangeText,
          since: props.customSince,
          until: props.customUntil,
          setSince: handleSetSince,
          setUntil: handleSetUntil,
        }}
        backLabel={translate('Back')}
        resetLabel={translate('Reset to Last 30 Days')}
        onReset={handleReset}
        parentOpen={props.open}
      />
      <SetupExpenseReportCycleModal
        isOpen={showSetupCyclePopup}
        onClose={() => setShowSetupCyclePopup(false)}
        cycleId={props.cycleId || ''}
      />
    </div>
  )
}

export default DateFilterDropdown
